import React, { SyntheticEvent, useContext, useState } from "react";

import {
  Alert,
  Button,
  CircularProgress,
  Grid,
  Snackbar,
  TextField,
} from "@mui/material";
import { useParams } from "react-router-dom";

import {
  Role,
  SubscriptionType,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import ReasonDropdown from "./shared/reason-dropdown";
import SubscriptionAction from "./subscription-action";
import { cancelSubscription } from "api/endpoints/subscription";
import { callOptionsHeaders } from "api/helpers";
import ConfirmDialog from "components/shared/confirm-dialog";
import {
  DEFAULT_SNACKBAR_DURATION,
  EventTypeReason,
  NO_DISPLAY_DATA,
} from "constants/index";
import { OrgDataContext } from "context/organisation-context";
import { SnackbarContext } from "context/snackbar-context";
import { SubscriptionActionsContext } from "context/subscriptions-action-context";
import {
  CancelSubscriptionWrapperProps,
  UrlParamTypes,
} from "types/org-display";

const CancelSubscription = ({
  refreshOrgData,
}: CancelSubscriptionWrapperProps) => {
  const { regionCode } = useParams<UrlParamTypes>();
  const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [cancelMessage, setCancelMessage] = useState("");
  const [openSuccessAlert, setOpenSuccessAlert] = useState(false);
  const { orgData, setOrgData } = useContext(OrgDataContext);
  const { showError } = useContext(SnackbarContext);
  const { isSubscriptionActionLoading, setIsSubscriptionActionLoading } =
    useContext(SubscriptionActionsContext);

  const subscription = orgData?.subscription?.[0];

  const [cancellationReason, setCancellationReason] = useState<string>("");

  const handleSetCancelationReason = (value: string) => {
    setCancellationReason(value);
  };

  const isCancelSubscriptionButtonDisabled =
    isSubscriptionActionLoading ||
    subscription?.type === SubscriptionType.FREE_ORG ||
    subscription?.active === false;

  const isConfirmationButtonDisabled = cancellationReason.length == 0;

  const isActiveDisplay = () => {
    if (subscription?.active === undefined) {
      return NO_DISPLAY_DATA;
    }
    return subscription?.active ? "Active" : "Inactive";
  };

  const handleCancelSubscriptionState = (
    cancelledSubscriptionStateActive: boolean,
  ) => {
    const newOrgData = { ...orgData } as Role;
    if (newOrgData.subscription && newOrgData.subscription[0].chargify) {
      newOrgData.subscription[0].active = cancelledSubscriptionStateActive;
      setOrgData(newOrgData);
    }
  };

  const handleClose = (
    event?: Event | SyntheticEvent<unknown, Event>,
    reason?: string | undefined,
  ) => {
    if (reason === EventTypeReason.Clickaway) {
      return;
    }
    setOpenSuccessAlert(false);
  };

  const handleCancelSubscription = () => {
    setShowLoadingSpinner(true);
    setIsSubscriptionActionLoading(true);
    const cancellationContext =
      cancelMessage && cancelMessage.trim().length > 0
        ? cancelMessage
        : "(Additional context not provided)";
    cancelSubscription(
      {
        subscriptionId: subscription.id,
        message: `${cancellationReason} | ${cancellationContext}`,
      },
      callOptionsHeaders(regionCode, orgData.id),
    )
      .then(() => {
        refreshOrgData();
        setOpenCancelDialog(false);
        setOpenSuccessAlert(true);
        handleCancelSubscriptionState(subscription.active);
        setCancelMessage("");
      })
      .catch(showError)
      .finally(() => {
        setShowLoadingSpinner(false);
        setIsSubscriptionActionLoading(false);
      });
  };

  return (
    <>
      <SubscriptionAction title="Status:" displayInfo={isActiveDisplay()}>
        {showLoadingSpinner ? (
          <Grid container justifyContent="center" sx={{ mt: 1, mr: 2 }}>
            <CircularProgress
              color="primary"
              variant="indeterminate"
              thickness={4}
              size={30}
            />
          </Grid>
        ) : (
          <Button
            sx={{ mt: 1, mr: 2 }}
            data-testid="btn-cancel-subscription"
            variant="contained"
            color="secondary"
            fullWidth
            disabled={isCancelSubscriptionButtonDisabled}
            onClick={() => setOpenCancelDialog(true)}
          >
            CANCEL SUBSCRIPTION
          </Button>
        )}
      </SubscriptionAction>

      <ConfirmDialog
        title="Cancel subscription"
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Confirm"
        open={openCancelDialog}
        setOpen={setOpenCancelDialog}
        onConfirm={() => {
          handleCancelSubscription();
        }}
        onCancel={() => {
          setOpenCancelDialog(false);
        }}
        disabled={isConfirmationButtonDisabled}
      >
        <Grid
          container
          direction="column"
          justifyContent="flex-start"
          paddingTop={"20px"}
        >
          <Grid item>
            <ReasonDropdown
              value={cancellationReason}
              onChange={handleSetCancelationReason}
              label="Cancellation reason (required)"
              id="cancellation-select"
              labelId="cancellation-select-label"
              dataAnchorPrefix="cancellation"
              dataTestIdPrefix="cancellation"
            />
            <Grid item xs={12} paddingTop={"20px"}>
              <TextField
                // eslint-disable-next-line
                inputProps={
                  { "data-testid": "cancel-reason-text-field" } as any
                }
                InputLabelProps={
                  // eslint-disable-next-line
                  { "data-testid": "cancel-reason-placeholder-label" } as any
                }
                fullWidth
                label="Additional cancellation details (optional)"
                multiline
                rows={5}
                variant={"outlined"}
                onChange={(e) => setCancelMessage(e.target.value)}
                value={cancelMessage}
              />
            </Grid>
          </Grid>
        </Grid>
      </ConfirmDialog>

      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={openSuccessAlert}
        autoHideDuration={DEFAULT_SNACKBAR_DURATION}
        onClose={handleClose}
        data-testid="snackbar-cancel-subscription"
      >
        <Alert severity="success" variant="filled">
          Canceled subscription successfully
        </Alert>
      </Snackbar>
    </>
  );
};

export default CancelSubscription;
